import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { UserCircleIcon, CircleStackIcon, CreditCardIcon, ClockIcon, DocumentTextIcon, BookOpenIcon, PrinterIcon, ShieldCheckIcon, TicketIcon, BellIcon } from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { clsx } from 'clsx';

const menuItems = [
  { name: 'My Library', href: '/library', icon: BookOpenIcon },
  { name: 'Credits', href: '/account/credits', icon: CircleStackIcon },
  { name: 'Notifications', href: '/account/notifications', icon: BellIcon },
  { name: 'Activity Feed', href: '/account/activity', icon: ClockIcon },
  { name: 'Purchase History', href: '/account/purchases', icon: DocumentTextIcon },
  { name: 'Redeem Coupon', href: '/account/coupons', icon: TicketIcon },
  { name: 'Print Jobs', href: '/account/print-jobs', icon: PrinterIcon },
];

export default function UserMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userData } = useAuth();

  const isAdmin = userData?.isAdmin === true;

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // Get current location and add it as a query parameter for post-login redirect
      const fromPath = location.pathname;
      navigate(`/login?from=${encodeURIComponent(fromPath)}`);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) {
    return (
      <button
        onClick={() => navigate('/login')}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
      >
        Login
      </button>
    );
  }

  return (
    <Menu as="div" className="relative ml-3">
      <Menu.Button className="flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
        <span className="sr-only">Open user menu</span>
        <UserCircleIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {menuItems.map((item) => (
            <Menu.Item key={item.href}>
              {({ active }) => (
                <button
                  onClick={() => navigate(item.href)}
                  className={clsx(
                    active ? 'bg-gray-50' : '',
                    'flex w-full items-center px-4 py-2 text-sm text-gray-700'
                  )}
                >
                  <item.icon className="h-5 w-5 mr-3 text-gray-400" aria-hidden="true" />
                  {item.name}
                </button>
              )}
            </Menu.Item>
          ))}
          
          {isAdmin && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate('/admin')}
                    className={clsx(
                      active ? 'bg-gray-50' : '',
                      'flex w-full items-center px-4 py-2 text-sm text-gray-700'
                    )}
                  >
                    <ShieldCheckIcon className="h-5 w-5 mr-3 text-gray-400" aria-hidden="true" />
                    Admin Dashboard
                  </button>
                )}
              </Menu.Item>
            </>
          )}
          
          <div className="border-t border-gray-100 my-1" />
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={handleSignOut}
                className={clsx(
                  active ? 'bg-gray-50' : '',
                  'block w-full text-left px-4 py-2 text-sm text-gray-700'
                )}
              >
                Sign Out
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}